import { useRouter } from 'next/router';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';

interface BreadcrumbItem {
  label: string;
  url: string;
  isLast: boolean;
}

const Breadcrumb = ({ fallbackUrl = '/' }) => {
  const { t } = useTranslation('common');
  const router = useRouter();

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    // Remove any query parameters
    const asPathWithoutQuery = router.asPath.split('?')[0];

    // Split pathname into segments
    const segments = asPathWithoutQuery.split('/').filter((v) => v.length > 0);

    // Reduce the segments into breadcrumb items
    return segments.reduce<BreadcrumbItem[]>((acc, curr, idx) => {
      // Build the URL for the current breadcrumb
      const url = '/' + segments.slice(0, idx + 1).join('/');

      // Format the label (remove dashes/underscores, capitalize)
      const label = curr
        .replace(/-/g, ' ')
        .replace(/\[.*?\]/g, '') // Remove route parameters like [id]
        .replace(/^\w/, (c) => c.toUpperCase()); // Capitalize first letter

      // Add breadcrumb item to accumulator
      acc.push({
        label,
        url,
        isLast: idx === segments.length - 1,
      });

      return acc;
    }, []);
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <div className="sticky top-0 z-20 -mx-4 mb-1 -mt-2 flex items-center bg-white p-4 dark:bg-black sm:static sm:top-auto sm:z-0 sm:m-0 sm:mb-4 sm:bg-transparent sm:p-0 sm:dark:bg-transparent">
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="inline-flex items-start space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              href="/"
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              {t('text-home')}
            </Link>
          </li>

          {breadcrumbs.map((breadcrumb, index) => (
            <li key={breadcrumb.url}>
              <div className="flex min-w-0 items-start">
                <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-gray-400" />
                {breadcrumb.isLast ? (
                  <span className="ml-1 break-words text-sm font-medium text-gray-700 dark:text-gray-300 md:ml-2">
                    {breadcrumb.label}
                  </span>
                ) : (
                  <Link
                    href={breadcrumb.url}
                    className="ml-1 break-words text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white md:ml-2"
                  >
                    {breadcrumb.label}
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
};

export default Breadcrumb;
